import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { Link, useLocation } from 'react-router-dom';
import { usePubNub } from 'pubnub-react';

import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';

import NavContext from 'app/contexts/NavContext';
import HeaderSearchContainer from 'app/containers/HeaderSearchContainer';
import SimpleHeader from 'app/components/SimpleHeader';
import { useGetPubNubConfigQuery, useGetAppConfigQuery } from 'app/api/mainApi';
import usePubNubSubscriptions from 'app/hooks/usePubNubSubscriptions';
import { HAS_UNREAD_CHAT_MESSAGES } from 'app/constants/pubnubActionTypes';

const HamburgerMenuIcon = styled(MenuIcon)(() => ({
  '&.MuiSvgIcon-root ': {
    color: 'white',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
  },
}));

const Header = () => {
  // NOTE: When we refactor layouts out of the routing in App.jsx, we can remove this check.
  const location = useLocation();
  const { pathname } = location;
  const { toggleExpandedNav } = useContext(NavContext);
  const useSimpleHeaderBecauseLayoutsAreInRouting = pathname.startsWith('/call/listing/');
  const pubNubClient = usePubNub();
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const pubNubConfig = useGetPubNubConfigQuery().data;
  const appConfig = useGetAppConfigQuery().data;
  const currentUser = appConfig?.current_user;
  const { doPubNubSubscribe, doPubNubUnsubscribe } = usePubNubSubscriptions();

  const handlePubNubMessage = useCallback((msg) => {
    if (msg.message.action !== HAS_UNREAD_CHAT_MESSAGES) return;
    setHasUnreadMessages(msg.message.payload.has_unread_chat_messages);
  });

  useEffect(() => {
    doPubNubSubscribe({
      pubNub: pubNubClient,
      channels: [pubNubConfig.pubnub_private_chat_channel],
      callback: handlePubNubMessage,
    });

    return () => {
      if (pubNubClient) {
        doPubNubUnsubscribe({
          pubNub: pubNubClient,
          channels: [pubNubConfig.pubnub_private_chat_channel],
          callback: handlePubNubMessage,
        });
      }
    };
  }, [pubNubClient]);

  useEffect(() => {
    if (!currentUser) return;
    setHasUnreadMessages(currentUser.hasUnreadChatMessages);
  }, [currentUser]);

  if (useSimpleHeaderBecauseLayoutsAreInRouting) {
    return <SimpleHeader />;
  }

  return (
    <header>
      <div className="container-max">
        <div className="container-header">
          <div className="row">
            <div className="col-sm-12 start-sm col-xs-12 center-xs">
              <div className="container-fluid">
                <div className="row start-sm middle-xs around-xs" style={{position: 'relative'}}>
                  <div className="nav-hamburger">
                    <HamburgerMenuIcon
                      sx={{
                        display: {
                          xs: 'block',
                          md: 'none',
                        },
                      }}
                      tabIndex={0}
                      onClick={toggleExpandedNav}
                    />
                  </div>
                  <div className="nfp-logo-container">
                    <Link
                      title="NiteFlirt"
                      reloadDocument
                      to="/"
                      role="link"
                      tabIndex={0}
                    >
                      <img src="https://niteflirt-assets.s3.amazonaws.com/homepage/niteflirt-logo-mob.png" alt="NiteFlirt" />
                    </Link>
                  </div>
                  <div id="box-menu">
                    <ul>
                      <li className="btn_find">
                        <Link
                          title="Phone Sex"
                          reloadDocument
                          to="/search"
                          role="link"
                          tabIndex={0}
                        >
                          PHONE SEX
                        </Link>
                        <span />
                      </li>
                      <li className="btn_chat">
                        {hasUnreadMessages && <div className="chat-dot" />}
                        <Link
                          title="Chat"
                          // we only need to reload the document while the
                          // chat app is still in the old layout.
                          // Once we turn on the FF for the new chat app,
                          // we can remove the reloadDocument prop.
                          reloadDocument
                          to="/chat"
                          role="link"
                          tabIndex={0}
                        >
                          CHAT
                        </Link>
                        <span />
                      </li>
                      <li className="btn_goody_search">
                        <Link
                          title="Goodies"
                          to="/goody/search#recent_additions"
                          role="link"
                          tabIndex={0}
                        >
                          GOODIES
                        </Link>
                        <span />
                      </li>
                      <li className="btn_feeds">
                        <Link
                          title="Feeds"
                          to="/feed/explore"
                          role="link"
                          tabIndex={0}
                        >
                          FEEDS
                        </Link>
                        <span />
                      </li>
                      <li className="btn_account">
                        <Link
                          // we only need to reload the document while the
                          // advisor and seeker accounts are still in the old layout.
                          // Once we turn on the FF for the new account pages
                          // we can remove the reloadDocument prop.
                          reloadDocument
                          title="Account"
                          to="/account"
                          role="link"
                          tabIndex={0}
                        >
                          ACCOUNT
                        </Link>
                        <span />
                      </li>
                      <li className="search">
                        <HeaderSearchContainer />
                      </li>
                      <li>
                        <span />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
